import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Badge, GoogleIcon } from '@/components/ui';
import { useAuth } from '@/hooks';
import { WAITLIST_URL } from '@/config/urls';
import { UnsupportedAccountNotice } from './UnsupportedAccountNotice';
import { HeroAlert, type HeroAlertTone } from './HeroAlert';
import { ONBOARDING_COPY } from './onboardingCopy';
import {
  invitationTokenFromPath,
  rememberInvitationHeroSeen,
  rememberInvitationToken,
} from '@/lib/invitationContext';

interface StepWelcomeProps {
  onComplete: () => void;
  invitation?: {
    householdName: string;
    inviterFirstName: string;
    inviteeEmail: string;
  };
  /**
   * Inline alert rendered above the CTA stack. The Hero itself never changes
   * shape for an invite or an expired link — only this slot does.
   */
  alert?: {
    tone: HeroAlertTone;
    title: string;
    body: string;
    onDismiss?: () => void;
  };
}

export function StepWelcome({
  onComplete,
  invitation,
  alert,
}: StepWelcomeProps) {
  const copy = ONBOARDING_COPY.welcome;
  const {
    signInWithGoogle,
    signOut,
    user,
    unsupportedNotice,
    clearUnsupportedNotice,
    signInError,
    clearSignInError,
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Household invitation deep links arrive with `?invitation=<id>` (from the
  // magic link generated by the household-invitations edge function) or
  // `?invited=true` (explicit flag for test fixtures). Either triggers the
  // invited-mode copy + CTA. The auth path is identical in both cases:
  // Google OAuth → provision_user matches the user's email against
  // caregiver_invitations server-side.
  const { inviteToken } = useParams<{ inviteToken: string }>();
  const pathInviteToken =
    inviteToken ?? invitationTokenFromPath(window.location.pathname);
  const accountMismatch =
    invitation !== undefined &&
    user?.email !== undefined &&
    user.email.toLowerCase() !== invitation.inviteeEmail.toLowerCase();

  /**
   * Clear the spinner when the browser restores this page from the
   * back/forward cache.
   *
   * `handleContinue` sets `loading` and then hands the tab to Google. If the
   * parent backs out of the consent screen instead of completing it, Safari
   * and Chrome restore the Hero from bfcache — same document, same React
   * state — so `loading` was still `true` and "Continue with Google" came
   * back permanently spinning with no way to retry (YAR-154). A remount
   * would have reset it, which is exactly why bfcache is the only path that
   * reproduces this. `event.persisted` is what distinguishes a bfcache
   * restore from an ordinary first load.
   */
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setLoading(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const handleContinue = async () => {
    if (user && !accountMismatch) {
      // Already authenticated (returning from OAuth or already logged in)
      if (pathInviteToken) rememberInvitationHeroSeen(pathInviteToken);
      onComplete();
      return;
    }

    clearUnsupportedNotice();
    clearSignInError();
    setError(null);
    setLoading(true);
    try {
      if (pathInviteToken) {
        rememberInvitationToken(pathInviteToken);
        rememberInvitationHeroSeen(pathInviteToken);
      }
      const redirectPath = pathInviteToken ? `/invite/${pathInviteToken}` : '/';
      if (accountMismatch) await signOut();
      await signInWithGoogle(
        `${window.location.origin}${redirectPath}`,
        accountMismatch,
      );
      // OAuth redirect will happen — user returns authenticated
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      setLoading(false);
    }
  };

  return (
    // Layout per Figma 7555:19769: hero (badge + logo + headline) is
    // vertically centered inside a flex-1 region; CTA stack sits flush
    // at the bottom with 20px page padding all around. `flex-1` on the
    // hero collapses the empty space into a single flexible spacer so
    // the headline stays optically centered as copy reflows.
    <div className="h-dvh flex flex-col px-5 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] bg-surface">
      {/* h-dvh + an inner overflow-y-auto region keeps the CTA cluster pinned
          to the viewport bottom on short viewports (George feedback #2). The
          hero uses my-auto to stay optically centered when there's room and
          to scroll from the top when it doesn't fit — avoids the
          justify-center + overflow clip. */}
      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
        {/* ── Hero: badge + logo + headline, optically centered ───── */}
        {/* 362px, not `max-w-sm` (384px): Figma's frame is 402 wide with
            20px page padding, so the content column measures exactly 362
            (7555:19769 → "362 × 594"). On a phone the two are identical —
            the viewport is the binding constraint — but on anything wider
            the old cap let the headline and subtitle run 22px past the
            frame, which is what QA measured as "title and subtitle
            containers are wider than the figma design" (YAR-154). */}
        <div className="my-auto flex flex-col items-center w-full max-w-[362px] mx-auto gap-10">
          {/* Badge above the logo ROW — Figma 9349:14568: Beta pill on top
            (4px gap), then a horizontal lockup of the 48px logo mark with
            the "Yaruta" wordmark to its right (4px gap, H3 32/40 bold). */}
          <div className="flex flex-col items-center gap-1">
            <Badge
              variant="info"
              size="sm"
            >
              {copy.badge}
            </Badge>

            <div className="flex items-center gap-1">
              <img
                src="/logo.svg"
                alt="Yaruta"
                width={48}
                height={48}
                className="block"
              />
              {/* Wordmark — Google's OAuth verification reviewer needs the
                app name visibly rendered on the consent entry point so it
                matches the OAuth project name. `/logo.svg` is just the
                leafy "Y" icon (no text), so we render the word here. */}
              <span className="text-[32px] leading-10 font-bold text-primary">
                Yaruta
              </span>
            </div>
          </div>

          {/* Headline. 40px / 48px line-height per Figma h2 token. Invited
            copy is a fixed string; the v2 marketing headline comes from
            ONBOARDING_COPY.welcome.headline as a single string and wraps
            naturally inside the max-w-sm container above — no manual
            line break. */}
          {/* Headline + subtitle share one frame with a 12px gap
              (`spacing/component/md`, Figma 7555:19769); the 40px hero gap
              only separates that frame from the logo (YAR-154, Oct 12). */}
          <div className="flex flex-col items-center gap-3">
            <h1 className="text-[40px] leading-[48px] font-bold text-primary text-center">
              {copy.headline}
            </h1>

            {/* Subtitle — the marketing line, in EVERY context. Both Welcome
            frames keep the headline and subtitle in full and carry the invite
            context in the alert card above the CTA instead; overwriting them
            left an invitee with no idea what the product was. */}
            {/* Figma `Body-lg/regular` (20/28) in `text/primary/default`
              (#264133) — it was rendering 18px in #5e5e5e because `text-lg`
              was silently falling back to Tailwind's default size and the
              colour used the neutral caption token (YAR-154). */}
            {/* One sentence per line (YAR-154, Oct 6): the frame breaks
              "Less to hold in your head." / "More room to be present." so
              the subtitle sits inside the headline's width instead of
              running wider than it. */}
            <p className="text-lg font-normal text-primary text-center">
              {copy.subtitle.split(/(?<=\.)\s+/).map((sentence, i) => (
                <span
                  key={sentence}
                  className="block"
                >
                  {/* The space keeps the paragraph's text the full sentence
                    pair for screen readers and copy-paste. */}
                  {i > 0 ? ' ' : ''}
                  {sentence}
                </span>
              ))}
            </p>
          </div>
        </div>
      </div>

      {/* ── CTA: button + waitlist + terms ──────────────────────── */}
      {/* Gap rhythm per Figma: 8px between Continue and waitlist (they
          read as one decision unit), 40px to the terms paragraph (so
          it lands as legal footer, not as a third option). shrink-0 keeps
          the cluster pinned at the bottom while the hero above scrolls. */}
      <div className="shrink-0 flex flex-col items-center w-full max-w-[362px] mx-auto gap-10">
        {/* Invite context / expired link — the `.Alerts` slot from the Welcome
            frames. First child of the bottom group, directly above the CTA
            stack. Dismiss only hides it; it must never navigate (AC8). */}
        {alert && (
          <HeroAlert
            tone={alert.tone}
            title={alert.title}
            body={alert.body}
            onDismiss={alert.onDismiss}
          />
        )}

        {/* Dismissible per YAR-154 AC8 — clearing the notice leaves the
            parent on the Hero with the CTA cluster intact, so they can retry
            (or not) without a second OAuth round trip just to hide the
            alert. AuthContext owns the notice, so its clearer is the
            dismiss handler.

            Sits HERE, as a sibling of the CTA group, rather than inside it:
            this is the same `.Alerts` slot HeroAlert occupies, and Figma puts
            40px (`spacing/component/4xl`) between an alert card and "Continue
            with Google". Nested in the group below it inherited that group's
            8px rhythm instead — the alert read as if it were part of the
            button stack (YAR-154). The invite alert above was always in the
            right slot, which is why only the unsupported-Gmail case showed
            the tight gap. */}
        {unsupportedNotice && (
          <UnsupportedAccountNotice
            reason={unsupportedNotice.reason}
            email={unsupportedNotice.email}
            variant="onboarding"
            onDismiss={clearUnsupportedNotice}
          />
        )}

        <div className="flex flex-col items-center w-full gap-2">
          {signInError && (
            <p
              className="text-sm text-text-error text-center"
              role="alert"
            >
              {signInError}
            </p>
          )}
          {error && (
            <p
              className="text-sm text-text-error text-center"
              role="alert"
            >
              {error}
            </p>
          )}

          {/* Continue with Google — overrides the outline variant's
              border (primary-hover green) and label color (primary
              green) so both read as neutral grays per the Figma spec.
              Border → #e5e5e5 (token: border). Label → #5e5e5e
              (token: muted-foreground). */}
          <Button
            variant="outline"
            fullWidth
            leftIcon={<GoogleIcon />}
            onClick={handleContinue}
            loading={loading}
            className="!border-border !text-muted-foreground"
          >
            {copy.googleCta}
          </Button>

          {/* Waitlist CTA — links out to the public early-access form on the
              marketing site (YAR-277). Rendered as an anchor, not a
              react-router button: the destination is external, and the old
              internal `/waitlist` route is a post-auth holding page that
              bounces a signed-out visitor straight back here — so the click
              looked dead. Opens in a new tab like the Terms/Privacy links
              below. Styled to match the Button `tertiary`/`sm` variant per
              Figma. Retained in invite context too — both Welcome frames
              (invited and expired) still draw it, and an invitee who arrives
              on a dead link needs the escape hatch. */}
          <a
            href={WAITLIST_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center font-semibold rounded-sm cursor-pointer transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary bg-transparent text-muted-foreground hover:bg-surface-secondary active:bg-surface-secondary px-1.5 py-1 text-base leading-6"
          >
            {copy.waitlist}
          </a>
        </div>

        {/* Terms — explicit break matches the Figma spec: first line
            "By continuing, you agree to our", second line "Terms of
            Service and Privacy Policy". Only the two links take #454545
            (`text/neutral/default-hover`); the rest stays #5e5e5e (YAR-154). */}
        <p className="text-sm text-muted-foreground text-center">
          {copy.consentPrefix}
          <br />
          {/* New tab so the legal pages don't replace the auth/onboarding
              entry screen (YAR-278). */}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-neutral-hover underline"
          >
            {copy.terms}
          </a>{' '}
          {copy.and}{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-neutral-hover underline"
          >
            {copy.privacy}
          </a>
          {/* Sentence-style microcopy keeps its period (Dev Notes global
              rule; Figma 9349:14568 renders "…Privacy Policy."). Matches the
              identical legal line on the digest step. */}
          .
        </p>
      </div>
    </div>
  );
}
